import { type Vec2, smoothstep } from '../core/math';
import type { Rng } from './rng';
import { curvature, normals, smoothClosed, smoothSeries } from './geometry';

/**
 * v2-generator-only shaping pass.
 *
 * The base scatter-and-hull method produces a fair mix of corners but rarely a
 * section with real character — a corner that keeps opening onto the straight, a
 * genuinely quick left-right, a hairpin that makes you stop and go. This pass
 * takes an already-valid centerline and sculpts one or two such sections into
 * it by pushing points along the local normal on a smooth ramp.
 *
 * Everything here is deterministic (only `Rng`, and only `+ - * /` and the
 * IEEE-754-exact `Math.sqrt` reached through `smoothstep`) so a date still
 * regenerates byte-identically forever. Every push is bounded well inside the
 * runoff, and the caller re-runs curvature limiting and the
 * self-intersection / length checks afterwards — a deformation that would break
 * the layout is simply thrown out with the seed, like any other bad attempt.
 *
 * `poly` is a uniform-spaced closed ring (SPACING metres apart). Returns a new
 * ring; never mutates the input. Returns it unchanged when nothing safe fits.
 */
export function sculptSections(poly: Vec2[], spacing: number, rng: Rng): Vec2[] {
  const n = poly.length;
  if (n < 60) return poly;

  const k = smoothSeries(curvature(poly), 2);
  const nrm = normals(poly);
  const out = poly.map((p) => ({ x: p.x, y: p.y }));

  // Contiguous runs of corner and of straight, in index order. Runs that would
  // wrap the seam are skipped — there are always plenty that do not.
  const CORNER_K = 1 / 60;
  const STRAIGHT_K = 0.012;
  const classOf = (idx: number): 0 | 1 | 2 =>
    Math.abs(k[idx]) >= CORNER_K ? 2 : Math.abs(k[idx]) < STRAIGHT_K ? 1 : 0;

  const corners: Array<{ from: number; to: number; apex: number; dir: number }> = [];
  const straights: Array<{ from: number; to: number }> = [];
  let i = 0;
  while (i < n) {
    const cls = classOf(i);
    let e = i;
    while (e + 1 < n && classOf(e + 1) === cls) e++;
    if (cls === 2) {
      let apex = i;
      let peak = 0;
      let signSum = 0;
      for (let j = i; j <= e; j++) {
        signSum += k[j];
        if (Math.abs(k[j]) > peak) {
          peak = Math.abs(k[j]);
          apex = j;
        }
      }
      corners.push({ from: i, to: e, apex, dir: signSum >= 0 ? 1 : -1 });
    } else if (cls === 1) {
      straights.push({ from: i, to: e });
    }
    i = e + 1;
  }

  /** Add `amp * ramp(t)` of normal offset across [centre-half, centre+half]. */
  const pushHump = (centre: number, half: number, amp: number): void => {
    for (let d = -half; d <= half; d++) {
      const idx = centre + d;
      if (idx < 1 || idx >= n - 1) continue;
      const t = (d + half) / (2 * half); // 0..1 across the window
      const ramp = t < 0.5 ? smoothstep(0, 0.5, t) : smoothstep(1, 0.5, t); // 0→1→0
      out[idx].x += nrm[idx].x * amp * ramp;
      out[idx].y += nrm[idx].y * amp * ramp;
    }
  };

  /** Ramp that climbs 0→1 over the first `rise` and eases 1→0 over the tail. */
  const pushRamp = (start: number, len: number, riseFrac: number, dirSign: number, amp: number): void => {
    for (let s = 0; s <= len; s++) {
      const idx = start + s;
      if (idx < 1 || idx >= n - 1) continue;
      const t = s / len;
      const shape = t < riseFrac ? smoothstep(0, riseFrac, t) : smoothstep(1, riseFrac, t);
      out[idx].x += nrm[idx].x * dirSign * amp * shape;
      out[idx].y += nrm[idx].y * dirSign * amp * shape;
    }
  };

  const perM = 1 / spacing; // samples per metre
  let applied = 0;
  const roll = rng.next();
  const wanted = roll < 0.12 ? 0 : roll < 0.62 ? 1 : 2;

  // Shuffle candidate transforms deterministically and try them until `wanted`
  // have taken. Each transform picks its own region and bails if none fits.
  const parabolica = (): boolean => {
    const usable = corners.filter((c) => c.to - c.from >= 12 * perM && c.apex + 24 * perM < n - 2);
    if (!usable.length) return false;
    const c = usable[rng.int(0, usable.length)];
    // Inside of the corner is +dir along the normal; opening the exit means
    // pushing the centerline the other way as the corner unwinds.
    const exitLen = Math.round((14 + rng.range(0, 10)) * perM);
    pushRamp(c.apex, exitLen, 0.35, -c.dir, 3 + rng.range(0, 1.6));
    // A touch of extra entry bite so it reads as one long corner, not a kink.
    pushHump(Math.max(1, c.from - Math.round(3 * perM)), Math.round(6 * perM), c.dir * 1.2);
    return true;
  };

  const esses = (): boolean => {
    const usable = straights.filter((s) => s.to - s.from >= 30 * perM);
    if (!usable.length) return false;
    const s = usable[rng.int(0, usable.length)];
    const mid = Math.round((s.from + s.to) / 2);
    const step = Math.round((10 + rng.range(0, 4)) * perM);
    const half = Math.round(8 * perM);
    const amp = 2.4 + rng.range(0, 0.8);
    const lobes = s.to - s.from >= 44 * perM ? 4 : 3;
    for (let l = 0; l < lobes; l++) {
      const centre = mid + (l - (lobes - 1) / 2) * step;
      const sign = l % 2 === 0 ? 1 : -1;
      const edge = l === 0 || l === lobes - 1 ? 0.6 : 1;
      pushHump(Math.round(centre), half, sign * amp * edge);
    }
    return true;
  };

  const hairpinBite = (): boolean => {
    const usable = corners.filter((c) => c.to - c.from >= 8 * perM && Math.abs(k[c.apex]) < 1 / 14);
    if (!usable.length) return false;
    const c = usable[rng.int(0, usable.length)];
    // Push the apex toward the inside to tighten it into a stop-and-go.
    pushHump(c.apex, Math.round(5 * perM), c.dir * (1.4 + rng.range(0, 0.8)));
    return true;
  };

  const order = [parabolica, esses, hairpinBite];
  // Fisher-Yates with the ring rng so the pick order is seed-stable.
  for (let a = order.length - 1; a > 0; a--) {
    const b = rng.int(0, a + 1);
    [order[a], order[b]] = [order[b], order[a]];
  }
  for (const transform of order) {
    if (applied >= wanted) break;
    if (transform()) applied++;
  }
  if (applied === 0) return poly;

  // Knock any sharp joint off the pushed sections; the caller's curvature
  // limiter and resample do the rest.
  return smoothClosed(out, 0.12, 1);
}
